import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AnalysisSubject } from 'venn-utils';
import type { BenchmarkConstraint } from 'venn-utils';
import { GetColor, Notifications, NotificationType, ZIndex } from 'venn-ui-kit';
import { isNil } from 'lodash';
import { useRecoilValue } from 'recoil';
import { proxySetIdState } from 'venn-state';
import type { PortfolioCompare } from 'venn-api';
import { getFund } from 'venn-api';
import type { SearchMenuItem } from '../../search-menu/types';
import { getBenchmarkSearchResponsiveProps, SearchMenuBar } from '../../search-menu';
import CreateCompositeFooter from '../../search-menu/components/CreateCompositeFooter';
import { getDisabledPortfolioTooltipMessage } from '../../modals';
import CompositeBenchmarkModal from '../../modals/composite-benchmark-modal/CompositeBenchmarkModal';
import NumberInput from '../../input/NumberInput';
import { MAIN_CONTENT_ID } from '../../skip-to-content/SkipToContent';

interface BenchmarkConstraintManagementProps {
  constraint: BenchmarkConstraint | undefined;
  constraintBenchmarkSubject: AnalysisSubject | undefined;
  onChangeBenchmarkSubject: (subject: AnalysisSubject | undefined) => void;
  onChangeMaxTrackingError: (value: number | undefined) => void;
  smallScreen?: boolean | undefined;
}

const isResultDisabled = ({ value }: SearchMenuItem) => !!value?.isHistoricalPortfolio;

const BenchmarkConstraintManagement = ({
  constraint,
  constraintBenchmarkSubject,
  onChangeBenchmarkSubject,
  onChangeMaxTrackingError,
  smallScreen,
}: BenchmarkConstraintManagementProps) => {
  const proxySetId = useRecoilValue(proxySetIdState);
  const [isCompositeModalOpen, setIsCompositeModalOpen] = useState(false);
  const menuPortalTarget = useMemo(() => document.getElementById(MAIN_CONTENT_ID), []);

  const onSelected = useCallback(
    (selectedMenuItem: SearchMenuItem) => {
      if (selectedMenuItem.value) {
        onChangeBenchmarkSubject(selectedMenuItem.value);
      }
    },
    [onChangeBenchmarkSubject],
  );

  const onCreateBenchmark = useCallback(() => setIsCompositeModalOpen(true), []);
  const onCompositeModalClose = useCallback(() => setIsCompositeModalOpen(false), []);

  const onCompositeModalSubmit = useCallback(
    async (benchmark: PortfolioCompare) => {
      if (!benchmark.fundId) {
        Notifications.notify('Benchmark was created but could not be loaded.', NotificationType.ERROR);
        return;
      }
      let fund;
      try {
        fund = await getFund(benchmark.fundId, proxySetId);
      } catch (e) {
        Notifications.notify('Failed to load the benchmark.', NotificationType.ERROR);
        return;
      }
      onChangeBenchmarkSubject(new AnalysisSubject(fund.content, 'investment', proxySetId));
      Notifications.notify('Benchmark created successfully', NotificationType.SUCCESS);
    },
    [onChangeBenchmarkSubject, proxySetId],
  );

  return (
    <Container data-testid="benchmark-relative-constraint">
      <FieldLabel>Constraint Benchmark</FieldLabel>
      <SearchMenuBar
        autofocus={false}
        defaultMenuIsOpen={false}
        onSelected={onSelected}
        location="portfolioLabBenchmarkConstraintSelection"
        value={
          isNil(constraintBenchmarkSubject)
            ? null
            : {
                value: constraintBenchmarkSubject,
                label: constraintBenchmarkSubject.name,
                category: 'recent' as const,
              }
        }
        getOptionValue={(item: SearchMenuItem) => item?.value?.id?.toString() ?? ''}
        isOptionDisabled={isResultDisabled}
        optionDisabledTooltipContent={getDisabledPortfolioTooltipMessage}
        onClear={() => onChangeBenchmarkSubject(undefined)}
        footer={CreateCompositeFooter(onCreateBenchmark)}
        customPlaceholder="Search for or create a benchmark…"
        clearQueryOnBlur
        usePortal
        menuPortalZIndex={ZIndex.Cover}
        menuPortalTarget={menuPortalTarget}
        menuPortalScrollContainer={menuPortalTarget}
        privateAssetSearchMode="PUBLIC_ONLY"
        {...getBenchmarkSearchResponsiveProps(smallScreen, { widenMenu: false })}
      />
      <Spacer />
      <TrackingErrorRow>
        <TrackingErrorLabel>Maximum Tracking Error (annualized) ≤</TrackingErrorLabel>
        <NumberInput
          allowNull
          label="Maximum Tracking Error"
          value={
            isNil(constraint) || isNil(constraintBenchmarkSubject) || isNil(constraint.maxTrackingError)
              ? null
              : constraint.maxTrackingError * 100
          }
          onChange={(value) => onChangeMaxTrackingError(isNil(value) ? undefined : value / 100)}
          unitSymbol="%"
          precision={2}
          width={100}
          placeholder="0.00%"
          min={0}
          rejectOutOfRange
          disabled={isNil(constraintBenchmarkSubject)}
          hideSteppers
          controlled
        />
      </TrackingErrorRow>
      <HelpText>Limit how far the optimized portfolio can drift from the benchmark on a risk-adjusted basis.</HelpText>
      {isCompositeModalOpen && (
        <CompositeBenchmarkModal
          onClose={onCompositeModalClose}
          onSubmit={onCompositeModalSubmit}
          resultsFilter={isResultDisabled}
        />
      )}
    </Container>
  );
};

export default BenchmarkConstraintManagement;

const Container = styled.div`
  padding: 16px 16px 24px 16px;
  background-color: ${GetColor.GreyScale.Grey10};
`;

const FieldLabel = styled.div`
  font-size: 12px;
  font-weight: bold;
  color: ${GetColor.GreyScale.Grey80};
  margin-bottom: 4px;
`;

const Spacer = styled.div`
  height: 16px;
`;

const TrackingErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TrackingErrorLabel = styled.div`
  font-size: 12px;
  color: ${GetColor.GreyScale.Grey80};
`;

const HelpText = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: ${GetColor.GreyScale.Grey80};
`;
